import { useState, useEffect, useRef } from "react";
import { neon, Session, User } from "@/integrations/neon";
import { AuthPage } from "@/components/AuthPage";
import { DashboardWrapper } from "@/components/DashboardWrapper";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { useToast } from "@/hooks/use-toast";

const Index = () => {
  type View = "loading" | "auth" | "reset" | "dashboard";
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState<View>("loading");
  const [isRecovering, setIsRecovering] = useState(false);
  const isRecoveringRef = useRef(false);
  const { toast } = useToast();

  const enterRecoveryFlow = (session: Session | null) => {
    isRecoveringRef.current = true;
    setIsRecovering(true);
    setUser(session?.user ?? null);
    setView("reset");
  };

  const leaveRecoveryFlow = () => {
    isRecoveringRef.current = false;
    setIsRecovering(false);
  };

  useEffect(() => {
    let mounted = true;

    const clearAuthHash = () => {
      if (typeof window === "undefined") return;
      if (!window.location.hash) return;
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, document.title, newUrl);
    };

    const parseHashParams = () => {
      if (typeof window === "undefined") return null;
      const hash = window.location.hash;
      if (!hash || hash.length <= 1) return null;
      return new URLSearchParams(hash.substring(1));
    };

    const initializeSession = async () => {
      const { data: { session } } = await neon.auth.getSession();
      if (!mounted) return;

      // If onAuthStateChange already handled a PASSWORD_RECOVERY event, don't override the reset view.
      if (isRecoveringRef.current) return;

      const hashParams = parseHashParams();

      if (hashParams?.get("error")) {
        toast({
          title: "Authentication error",
          description: hashParams.get("error_description") ?? "The link is invalid or has expired.",
          variant: "destructive",
        });
        clearAuthHash();
        setUser(null);
        setView("auth");
        return;
      }

      const isRecovery = hashParams?.get("type") === "recovery";

      if (isRecovery) {
        enterRecoveryFlow(session);
        clearAuthHash();
        return;
      }

      if (session?.user) {
        setUser(session.user);
        setView("dashboard");
      } else {
        setUser(null);
        setView("auth");
      }
    };

    initializeSession();

    const { data: { subscription } } = neon.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        if (event === "PASSWORD_RECOVERY") {
          enterRecoveryFlow(session);
          clearAuthHash();
          return;
        }

        if (
          event === "SIGNED_IN" ||
          event === "TOKEN_REFRESHED" ||
          event === "USER_UPDATED" ||
          event === "INITIAL_SESSION"
        ) {
          setUser(session?.user ?? null);
          if (isRecoveringRef.current) {
            setView("reset");
          } else if (session?.user) {
            setView("dashboard");
          } else {
            setView("auth");
          }
          return;
        }

        if (event === "SIGNED_OUT") {
          setUser(null);
          setView("auth");
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [toast]);

  const handleSignOut = () => {
    neon.auth.signOut();
    setUser(null);
    setView("auth");
    leaveRecoveryFlow();
  };

  if (view === "loading") {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (view === "reset") {
    const handleResetSuccess = (updatedUser: User | null) => {
      setUser(updatedUser);
      leaveRecoveryFlow();
      setView(updatedUser ? "dashboard" : "auth");
    };

    const handleResetCancel = () => {
      neon.auth.signOut();
      setUser(null);
      leaveRecoveryFlow();
      setView("auth");
    };

    return (
      <PasswordResetForm
        onSuccess={handleResetSuccess}
        onCancel={handleResetCancel}
      />
    );
  }

  if (view === "auth") {
    return (
      <AuthPage
        onAuthSuccess={() => {
          neon.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setView(session?.user ? "dashboard" : "auth");
          });
        }}
      />
    );
  }

  return user
    ? <DashboardWrapper user={user} onSignOut={handleSignOut} />
    : (
      <AuthPage
        onAuthSuccess={() => {
          neon.auth.getSession().then(({ data: { session } }) => {
            setUser(session?.user ?? null);
            setView(session?.user ? "dashboard" : "auth");
          });
        }}
      />
    );
};

export default Index;
